import {
  Agreement,
  Mutation,
  UpdateApplicationResultTypenames,
} from '@varo/graphql-schema-types';
import { Href, useRouter } from 'expo-router';
import { NAV_ANIMATION_DURATION } from 'features/application/constants';
import { useApplicationTrackErrorEvents } from 'features/application/hooks/useApplicationTrackErrorEvents';
import { useEffect, useState } from 'react';
import { UpdateBankingApplication } from 'services/graphql/mutations/UpdateBankingApplication';
import useCancellableMutation from 'shared/hooks/useCancellableMutation';
import { AnyVariables } from 'urql';

export interface UpdateApplicationArgs {
  updates: AnyVariables;
  nextRoute?: Href;
  agreements?: Agreement[];
}

export type UpdateApplicationData = Pick<Mutation, 'updateApplication'>;

export type UpdateApplicationHandler = (
  args: UpdateApplicationArgs,
) => Promise<UpdateApplicationData | undefined>;

interface UseUpdateApplicationArgs {
  applicationId?: string;
  sourceName?: string;
}

const useUpdateApplication = ({
  applicationId,
  sourceName,
}: UseUpdateApplicationArgs = {}) => {
  const router = useRouter();
  const [isUpdateApplicationSuccess, setIsUpdateApplicationSuccess] =
    useState<boolean>(false);
  const [isUpdateApplicationLoading, setIsUpdateApplicationLoading] =
    useState<boolean>(false);
  const { logErrorEvent } = useApplicationTrackErrorEvents();

  const [updateApplicationResult, executeMutation] =
    useCancellableMutation<UpdateApplicationData>(UpdateBankingApplication);

  const updateApplicationRaw = updateApplicationResult.data?.updateApplication;

  const updateApplicationError =
    updateApplicationRaw?.__typename ===
    UpdateApplicationResultTypenames.UpdateApplicationError
      ? updateApplicationRaw
      : undefined;

  useEffect(() => {
    if (updateApplicationResult.error && sourceName) {
      logErrorEvent(updateApplicationResult.error.message, sourceName);
    }
    if (sourceName && updateApplicationError) {
      logErrorEvent(updateApplicationError.message, sourceName);
    }
  }, [
    logErrorEvent,
    sourceName,
    updateApplicationError,
    updateApplicationResult.error,
  ]);

  const updateApplication: UpdateApplicationHandler = async ({
    updates,
    nextRoute,
  }) => {
    if (applicationId || (updates && 'applicationId' in updates)) {
      setIsUpdateApplicationLoading(true);
      const { data: mutationData } = await executeMutation({
        applicationId,
        ...updates,
      });

      const response = mutationData?.updateApplication;

      if (
        response?.__typename ===
        UpdateApplicationResultTypenames.UpdateApplicationSuccess
      ) {
        setIsUpdateApplicationSuccess(true);
        setTimeout(() => {
          if (nextRoute) {
            router.replace(nextRoute);
          }
          setIsUpdateApplicationSuccess(false);
        }, NAV_ANIMATION_DURATION);
      }
      setTimeout(() => {
        setIsUpdateApplicationLoading(false);
      }, NAV_ANIMATION_DURATION + 30);

      return mutationData;
    }
  };

  return {
    updateApplication,
    isUpdateApplicationSuccess,
    updateApplicationResult,
    isUpdateApplicationLoading,
    updateApplicationError,
  };
};

export default useUpdateApplication;
